import AdminError from 'admin/components/common/ErrorBoundary/AdminError';
import { Hint, TextField } from 'admin/components/common/Form';
import { isRequired } from 'admin/components/common/Form/validations';

import { Context as ThemeContext } from 'admin/components/common/Theme';
import { ChildrenFormProp, fields } from 'admin/components/pages/Story/Flow/EditCardModal/Form';
import { useAdminSelector } from 'admin/reducers';
import { selectStoryCards } from 'admin/reducers/story-editor/selectors';
import produce from 'immer';
import React from 'react';
import { Field, Validator } from 'redux-form';
import type { StoryVersionType } from 'types/story';
import { StoryFacade } from 'utils/facades';
import translate from 'utils/translate';

type Props = {
	form: ChildrenFormProp;
};

const t = (path: string) => translate(`story.editCardModal.fields.${fields._id}.${path}`);

const useCardIdValidators = (initialCardId: string): Validator[] => {
	const cards = useAdminSelector(selectStoryCards);

	const otherCardIds = React.useMemo(() => {
		return cards.reduce((acc: string[], card) => {
			if (card._id !== initialCardId) acc.push(card._id);
			return acc;
		}, []);
	}, [cards, initialCardId]);

	return React.useMemo(() => {
		return [
			(v: string) => (isRequired(v) ? undefined : t('required')),
			(v: string) => (!otherCardIds.includes(v) ? undefined : t('uniq')),
		];
	}, [otherCardIds]);
};

const CardIdEditor: React.FC<Props> = props => {
	const { theme } = React.useContext(ThemeContext);
	const validators = useCardIdValidators(props.form.initialValues._id ?? '');

	return (
		<Field
			name={fields._id}
			component={TextField}
			validate={validators}
			props={{
				theme,
				isLabelUppercase: false,
				isRequired: true,
				label: t('label'),
				showErrorsOn: 'dirty',
				description: (
					<Hint
						text={
							<>
								{t('hint')}
								<br />
								{t('warning')}
							</>
						}
						theme={theme}
					/>
				),
			}}
		/>
	);
};

interface UpdateStoryCardIdParams {
	story: StoryVersionType;
	oldId: string;
	newId: string;
}

export const updateStoryCardId = ({ story, oldId, newId }: UpdateStoryCardIdParams) => {
	const { card, step } = StoryFacade.findCardInSteps(oldId, story.data.steps);

	if (!card || !step) {
		throw new AdminError('Fatal error: step or card not found');
	}

	return produce(story, draftStory => {
		draftStory.data.steps[step.index].cards[card.index]._id = newId;

		// Update story card settings id
		const cardStorySettings = draftStory.settings.cards?.[oldId];
		if (cardStorySettings) {
			draftStory.settings.cards![newId] = { ...cardStorySettings };
			delete draftStory.settings.cards![oldId];
		}

		// Update card events that point to the old card id
		draftStory.data.steps.slice(0, step.index).forEach(_step => {
			_step.cards.forEach(_card => {
				_card.events.forEach(draftEvent => {
					if (draftEvent.connection?.pointId === oldId) {
						draftEvent.connection.pointId = newId;
					}
				});
			});
		});
	});
};

export default CardIdEditor;
